<script lang="ts">
  import { onMount } from 'svelte';
  import { ALL_MODES, formatDailyAverageScore } from '@atlasguess/games';
  import EmptyState from '../../components/ui/EmptyState.svelte';
  import Spinner from '../../components/ui/Spinner.svelte';
  import LeaderboardTable from '../../components/LeaderboardTable.svelte';
  import QuizLeaderboardTable from '../../components/QuizLeaderboardTable.svelte';
  import {
    fetchDailyLeaderboard,
    fetchQuizLeaderboard,
    type DailyLeaderboard,
    type QuizLeaderboard,
    type LeaderboardScope,
  } from '../../lib/leaderboards';
  import { user } from '../../lib/stores/auth';
  import { withPageTitle } from '../../lib/page-title';
  import { DAILY_ROUND_COUNT } from '../../lib/constants';

  // Public leaderboard surface. Two boards live here — the flagship
  // Daily Cities (date-scoped, score-driven) and one all-time best
  // board per registered country quiz. The mode tab strip across the
  // top picks which board renders below.
  //
  // Scope tabs (Global / Following) live inside each board's hero card
  // since "your follow feed" applies the same way regardless of which
  // mode is active. Following is hidden when anonymous (no user → no
  // follow set → no useful filter).
  //
  // URL mirrors:
  //   /leaderboard                                → daily, global
  //   /leaderboard?mode=countries-europe-quiz     → europe quiz, global
  //   /leaderboard?scope=following                → daily, following
  //   /leaderboard?mode=…&scope=following         → quiz, following

  type ActiveMode = 'daily' | string; // 'daily' or a quiz mode_id

  // Pull every registered quiz mode for the tab strip. Order matches
  // the registry (Europe, Africa, North America, South America, Asia,
  // Oceania today). Adding a new quiz: register it in the games
  // package, no SPA change needed here.
  const QUIZ_MODES = ALL_MODES.filter((m) => m.format === 'quiz');
  const QUIZ_MODE_IDS = new Set(QUIZ_MODES.map((m) => m.id));

  function readInitialMode(): ActiveMode {
    if (typeof window === 'undefined') return 'daily';
    const slug = new URL(window.location.href).searchParams.get('mode');
    if (slug && QUIZ_MODE_IDS.has(slug)) return slug;
    return 'daily';
  }

  function readInitialScope(): LeaderboardScope {
    if (typeof window === 'undefined') return 'global';
    const slug = new URL(window.location.href).searchParams.get('scope');
    return slug === 'following' ? 'following' : 'global';
  }

  let activeMode = $state<ActiveMode>(readInitialMode());
  let scope = $state<LeaderboardScope>(readInitialScope());

  // Two separate caches so the table that's about to render doesn't
  // briefly show stale data from the OTHER mode while a fetch is in
  // flight. We null the inactive one's view via the template branch.
  let dailyBoard = $state<DailyLeaderboard | null>(null);
  let quizBoard = $state<QuizLeaderboard | null>(null);
  let loading = $state(true);
  let error = $state(false);

  $effect(() => withPageTitle('Leaderboard'));

  // Mirror activeMode + scope to URL. Both default values are
  // dropped from the query string so canonical /leaderboard stays
  // clean when the user is on the daily-global combo.
  $effect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (activeMode === 'daily') {
      url.searchParams.delete('mode');
    } else {
      url.searchParams.set('mode', activeMode);
    }
    if (scope === 'global') {
      url.searchParams.delete('scope');
    } else {
      url.searchParams.set('scope', scope);
    }
    window.history.replaceState({}, '', url.toString());
  });

  async function load(): Promise<void> {
    loading = true;
    error = false;
    try {
      if (activeMode === 'daily') {
        dailyBoard = await fetchDailyLeaderboard(undefined, scope);
      } else {
        quizBoard = await fetchQuizLeaderboard(activeMode, scope);
      }
    } catch {
      error = true;
    } finally {
      loading = false;
    }
  }

  // If the user logs out while on the Following tab, fall back to
  // Global so they're not stranded on a tab that's about to disappear.
  // Safe to call load() inside the effect because load() awaits
  // network and doesn't synchronously re-trigger this branch.
  $effect(() => {
    if (!$user && scope === 'following') {
      scope = 'global';
      void load();
    }
  });

  function setMode(next: ActiveMode): void {
    if (next === activeMode) return;
    activeMode = next;
    void load();
  }

  function setScope(next: LeaderboardScope): void {
    if (next === scope) return;
    scope = next;
    void load();
  }

  onMount(() => {
    void load();
  });

  // Pretty-format the daily date once we have the payload. Parse the
  // YYYY-MM-DD as *local* midnight — appending 'T00:00:00Z' would
  // force UTC midnight, which `toLocaleDateString` then shifts back
  // into the user's zone (EST: 7pm previous evening) and prints the
  // wrong weekday.
  const dateLabel = $derived.by((): string => {
    if (!dailyBoard) return '';
    const [y, m, d] = dailyBoard.date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  });

  // Active quiz mode → registered config; used for hero header text
  // and the user-rank chip's mode reference.
  const activeQuiz = $derived(
    activeMode === 'daily' ? null : QUIZ_MODES.find((m) => m.id === activeMode) ?? null,
  );

  const userInTopHundred = $derived.by((): boolean => {
    if (!$user) return false;
    if (activeMode === 'daily') {
      return !!dailyBoard?.rows.some((r) => r.username === $user!.username);
    }
    return !!quizBoard?.rows.some((r) => r.username === $user!.username);
  });

  const isFollowingScopeEmpty = $derived.by((): boolean => {
    if (scope !== 'following') return false;
    if (activeMode === 'daily') return dailyBoard?.rows.length === 0;
    return quizBoard?.rows.length === 0;
  });

  // Format a quiz user_rank stat line: "40/43 · 4:32"
  function formatQuizRank(correct: number, total: number, ms: number): string {
    const totalSec = Math.round(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${correct}/${total} · ${m}:${String(s).padStart(2, '0')}`;
  }
</script>

<article class="leaderboards">
  <h1 class="leaderboards__title">Leaderboard</h1>

  <!-- Mode tab strip — Daily Cities first, then one tab per registered
       country quiz. Horizontal scroll on narrow screens so the strip
       never wraps; the active tab self-scrolls into view via the
       browser's default focus behavior on click. -->
  <div class="leaderboards__mode-tabs" role="tablist" aria-label="Leaderboard mode">
    <button
      type="button"
      role="tab"
      aria-selected={activeMode === 'daily'}
      class="leaderboards__mode-tab"
      class:leaderboards__mode-tab--active={activeMode === 'daily'}
      onclick={() => setMode('daily')}
    >
      Daily Cities
    </button>
    {#each QUIZ_MODES as quiz (quiz.id)}
      <button
        type="button"
        role="tab"
        aria-selected={activeMode === quiz.id}
        class="leaderboards__mode-tab"
        class:leaderboards__mode-tab--active={activeMode === quiz.id}
        onclick={() => setMode(quiz.id)}
      >
        {quiz.shortName ?? quiz.displayName}
      </button>
    {/each}
  </div>

  <section class="leaderboards__hero">
    <header class="leaderboards__hero-header">
      <div>
        {#if activeMode === 'daily'}
          <p class="leaderboards__hero-eyebrow">Today's daily</p>
          <h2 class="leaderboards__hero-title">Daily Cities</h2>
          {#if dateLabel}
            <p class="leaderboards__hero-date">{dateLabel}</p>
          {/if}
        {:else if activeQuiz}
          <p class="leaderboards__hero-eyebrow">All-time best</p>
          <h2 class="leaderboards__hero-title">{activeQuiz.displayName}</h2>
        {/if}
      </div>
    </header>

    <div class="leaderboards__tabs" role="tablist" aria-label="Leaderboard scope">
      <button
        type="button"
        role="tab"
        aria-selected={scope === 'global'}
        class="leaderboards__tab"
        class:leaderboards__tab--active={scope === 'global'}
        onclick={() => setScope('global')}
      >
        Global
      </button>
      {#if $user}
        <button
          type="button"
          role="tab"
          aria-selected={scope === 'following'}
          class="leaderboards__tab"
          class:leaderboards__tab--active={scope === 'following'}
          onclick={() => setScope('following')}
        >
          Following
        </button>
      {/if}
    </div>

    {#if loading}
      <div class="leaderboards__loading"><Spinner size="lg" /></div>
    {:else if error}
      <p class="leaderboards__error">Couldn't load the leaderboard. Try refreshing.</p>
    {:else if isFollowingScopeEmpty}
      <EmptyState
        title="Your follow feed is empty"
        body="Follow a few players to see their results alongside yours here."
      />
    {:else if activeMode === 'daily' && dailyBoard && dailyBoard.rows.length > 0}
      <LeaderboardTable
        rows={dailyBoard.rows}
        highlightUsername={$user?.username ?? null}
      />

      {#if dailyBoard.user_rank && !userInTopHundred}
        <p class="leaderboards__user-rank">
          You placed <strong>#{dailyBoard.user_rank.rank}</strong>
          with <strong>{formatDailyAverageScore(dailyBoard.user_rank.total_score, DAILY_ROUND_COUNT)}</strong>.
        </p>
      {:else if dailyBoard.user_rank}
        <p class="leaderboards__user-rank">
          You're <strong>#{dailyBoard.user_rank.rank}</strong>
          on {scope === 'following' ? 'your follow feed' : "today's board"}.
        </p>
      {/if}
    {:else if activeMode !== 'daily' && quizBoard && quizBoard.rows.length > 0}
      <QuizLeaderboardTable
        rows={quizBoard.rows}
        highlightUsername={$user?.username ?? null}
      />

      {#if quizBoard.user_rank && !userInTopHundred}
        <!-- Outside-top-100 placement chip: same shape as the daily
             variant but uses the quiz best-run line ("40/43 · 4:32"). -->
        <p class="leaderboards__user-rank">
          You placed <strong>#{quizBoard.user_rank.rank}</strong>
          with <strong>
            {formatQuizRank(
              quizBoard.user_rank.correct,
              quizBoard.user_rank.total,
              quizBoard.user_rank.ms,
            )}
          </strong>.
        </p>
      {:else if quizBoard.user_rank}
        <p class="leaderboards__user-rank">
          You're <strong>#{quizBoard.user_rank.rank}</strong>
          on {scope === 'following' ? 'your follow feed' : 'this quiz'}.
        </p>
      {/if}
    {:else}
      <!-- No rows yet — copy varies by mode so it reads naturally for
           never-played quizzes vs. an early-morning daily. -->
      {#if activeMode === 'daily'}
        <EmptyState
          title="No runs yet today"
          body="Be the first to play today's daily and claim the top spot."
        />
      {:else}
        <EmptyState
          title="No runs yet"
          body="Be the first to finish this quiz and claim the top spot."
        />
      {/if}
    {/if}
  </section>
</article>
